<template>
    <div ref="usableDisplayArea" :class="visibleClass">
        <crosstab-title
            v-if="!isTotal"
            :reportModel="reportModel"
            :control1="control1"
            :control2="control2"
        ></crosstab-title>
        <statistic-legend
            :statisticDefinitions="reportModel.usedStatistics"
        ></statistic-legend>
        <div
            ref="outerCrosstabWrapper"
            class="outer-crosstab-wrapper"
            v-if="reportModel !== null"
        >
            <div ref="crosstabWrapper" class="crosstab-wrapper" tabindex="0">
                <crosstab-table
                    ref="crosstabTableCtl"
                    v-if="
                        selectionModel.v.column.include &&
                        control2.levelsGroupedByRows != null
                    "
                    :models="models"
                    :control1="control1"
                    :control2="control2"
                    :reportModel="reportModel"
                    :levels="levels"
                    :total="total"
                    :rows="rows"
                    :columns="columns"
                ></crosstab-table>

                <!--Row only table-->
                <row-only
                    ref="crossTabulationRowOnlyCtl"
                    v-if="!selectionModel.v.column.include"
                    :levels="levels"
                    :reportModel="reportModel"
                    :row="reportModel.parms.row"
                    :rows="rows"
                ></row-only>
            </div>
            <div v-if="hasChiSquare" class="chi-square">
                <chi-square
                    ref="chiSquare"
                    :control1="control1"
                    :control2="control2"
                ></chi-square>
            </div>
            <div class="dataset-totals" v-if="isTotal">
                <dataset-totals
                    :recordTotals="reportModel.recordTotals"
                ></dataset-totals>
            </div>
            <div>
                <footnotes
                    ref="footnotes"
                    :recordTotals="reportModel.recordTotals"
                    :control1="control1"
                    :control2="control2"
                    :noSourceRecords="noSourceRecords"
                    :isDataUnreliable="isDataUnreliable"
                ></footnotes>
            </div>
            <div class="spacing-row"></div>
        </div>
    </div>
</template>

<script>
/* eslint no-unused-vars: 'off' */

import current from '@/scripts/current';
import common from '@/scripts/common';
import crosstabTitle from '@/components/crossTabulation/report/crosstabTitle';
import rowOnly from '@/components/crossTabulation/report/rowOnly';
import footnotes from '@/components/footnotes';
import crosstabTable from '@/components/crossTabulation/report/crosstabTable';
import chiSquare from '@/components/crossTabulation/report/chiSquare';
import statisticLegend from '@/components/crossTabulation/report/statisticLegend';
import datasetTotals from '@/components/datasetTotals';
import commonModelHelper from '@/scripts/commonModelHelper';
import CtColumn from '@/bo/CtColumn';
import CtReportCell from '@/bo/CtReportCell';
import xlsxBuilder from '@/scripts/crossTabulation/xlsxBuilder';
import XlsxSheetBuilder from '@/bo/XlsxSheetBuilder';
import store from '@/store';

function buildCtColumns(categories, controlLevels) {
    return categories.map(
        (c, i) =>
            new CtColumn(
                c.id,
                c.title,
                commonModelHelper.getNumberOfSourceRecordsCt(
                    controlLevels,
                    null,
                    c
                ),
                false
            )
    );
}
function buildRows(rowLevels, controlLevels) {
    return rowLevels.map((r) => ({
        level: r,
        totalSourceRecords: commonModelHelper.getNumberOfSourceRecordsCt(
            controlLevels,
            r,
            null
        )
    }));
}

function buildRowModels(
    rowLevel,
    columns,
    statisticDefinition,
    reportParms,
    totalSourceRecords
) {
    let isColumnTotal = rowLevel.rowLevel.id === 0;
    let models = columns.map(
        (c) =>
            new CtReportCell({
                //id, rowLevel, columnData, statisticDefinition, totalSourceRecords, reportParms, isRowTotal
                id: c.id,
                rowLevel: rowLevel,
                columnData: rowLevel.columnStatistics[c.id - 1],
                statisticDefinition: statisticDefinition,
                totalSourceRecords: isColumnTotal ? c.totalSourceRecords : null,
                reportParms: reportParms
            })
    );
    let hasTotal =
        rowLevel.rowLevel.id !== 0 ||
        statisticDefinition.apiName === 'sampleSize' ||
        statisticDefinition.apiName === 'weightedN';
    if (hasTotal)
        models.push(
            new CtReportCell({
                //id, rowLevel, columnData, statisticDefinition, totalSourceRecords, reportParms, isRowTotal
                rowLevel: rowLevel,
                columnData: rowLevel.totalStatistics,
                statisticDefinition: statisticDefinition,
                totalSourceRecords: totalSourceRecords,
                reportParms: reportParms,
                isRowTotal: true
            })
        );
    return models;
}

function buildModels(levels, columns, reportModel, rows, total) {
    let results = levels.map((l, li) =>
        reportModel.usedStatistics.map((s, si) =>
            buildRowModels(
                l,
                columns,
                s,
                reportModel.parms,
                rows[li].totalSourceRecords
            )
        )
    );
    //Add totals
    const totalStatistics = reportModel.usedStatistics.map((s, si) =>
        buildRowModels(total, columns, s, reportModel.parms, 1)
    );
    results.push(totalStatistics);
    return results;
}

function isControlLevel(control1LevelId, control2LevelId) {
    let vm = this;
    let mycontrol1LevelId = vm.controlLevels.control1
        ? vm.controlLevels.control1.id
        : 0;
    let mycontrol2LevelId = vm.controlLevels.control2
        ? vm.controlLevels.control2.id
        : 0;
    return (
        control1LevelId === mycontrol1LevelId &&
        control2LevelId === mycontrol2LevelId
    );
}

export default {
    props: ['control1', 'control2', 'reportModel', 'selectedLevels', 'isTotal'],
    created: function () {
        const vm = this;
        vm.visibleClass = isCrosstabVisible(vm, vm.selectedLevels)
            ? ''
            : 'hidden';
        vm.hasChiSquare =
            vm.reportModel.parms.showChiSquare && vm.control2.chiSquare != null;
    },
    data: function () {
        const vm = this;
        var controlLevels = {
            control1: vm.control1.control1Level,
            control2: vm.control2.control2Level
        };
        var totals = this.control2.levelsGroupedByRows.filter(
            (l) => l.rowLevel.id === 0
        );

        var totalSourceRecords =
            commonModelHelper.getNumberOfSourceRecordsCt(controlLevels);
        var levels = this.control2.levelsGroupedByRows.filter(
            (l) => l.rowLevel.id !== 0
        );
        var rowLevels = levels.map((r) => r.rowLevel);
        var rows = buildRows(rowLevels, controlLevels);
        var columns = vm.reportModel.parms.column
            ? buildCtColumns(
                  vm.reportModel.parms.column.categories,
                  controlLevels
              )
            : [];
        var noSourceRecords =
            totalSourceRecords === 0 ||
            rows.filter((r) => r.totalSourceRecords === 0).length > 0 ||
            columns.filter((c) => c.totalSourceRecords === 0).length > 0;
        const total = totals.length > 0 ? totals[0] : null;

        let results = {
            totalSourceRecords: totalSourceRecords,
            levels: levels,
            total: total,
            controlLevels: controlLevels,
            rows: rows,
            columns: columns,
            noSourceRecords: noSourceRecords,
            models: null,
            isDataUnreliable: false,
            visibleClass: 'hidden'
        };
        if (current.selectionModel.v.column.include) {
            results.models = buildModels(
                levels,
                columns,
                vm.reportModel,
                rows,
                total
            );
            results.isDataUnreliable =
                results.models.filter(
                    (model) =>
                        model.filter(
                            (statistic) =>
                                statistic.filter(
                                    (s) =>
                                        s.isSampleSizeIsTooLow ||
                                        s.isRelativeStandardErrorTooHigh
                                ).length > 0
                        ).length > 0
                ).length > 0;
        }
        return results;
    },
    computed: {
        selectionModel: function () {
            return current.selectionModel;
        }
    },
    components: {
        rowOnly,
        footnotes: footnotes,
        crosstabTitle,
        crosstabTable,
        chiSquare,
        statisticLegend,
        datasetTotals
    },
    mounted: function () {
        const vm = this;

        store.dispatch('loadedControls/mounted', {
            name: 'singleCrosstab',
            control1: vm.control1,
            control2: vm.control2,
            //getLeftMarginToRightJustify: vm.getLeftMarginToRightJustify,
            crosstabWrapper: vm.$refs.crosstabWrapper,
            usableDisplayArea: vm.$refs.usableDisplayArea,
            outerCrosstabWrapper: vm.$refs.outerCrosstabWrapper,
            hasChiSquare: vm.hasChiSquare
        });
    },
    beforeUnmount() {
        // Unregister the event listener before destroying this Vue instance
        window.removeEventListener('resize', this.onResize);
    },
    methods: {
        //getLeftMarginToRightJustify,
        reportFiltersChanged,
        createXlsxWorksheet,
        isControlLevel,
        // resizeControls: function () {
        //   var vm = this;
        //   if (vm.$refs && vm.reportModel && vm.reportModel.parms)
        //     ctResizer.resizeTable(vm);
        // },
        // onResize(event) {
        //   this.resizeControls();
        // },
        // setScroll: function () {
        //   this.$refs.crosstabWrapperOld.setAttribute("style", "overflow-x: auto");
        // },

        anySelected: function (levels) {
            if (
                levels === undefined ||
                levels === null ||
                levels.length === 0 ||
                levels[0] === 'All'
            )
                return false;
            else return true;
        }
    }
};

function reportFiltersChanged(values) {
    var vm = this;
    var isVisible = isCrosstabVisible(vm, values);
    vm.visibleClass = isVisible ? '' : 'hidden';
}

function isCrosstabVisible(vm, selectedLevels) {
    if (vm.isTotal) return true;
    if (!selectedLevels || !selectedLevels.control1) return false;

    //vm.control1, vm.control2, selectedLevels, vm.isTotal;

    if (
        !current.selectionModel.v.control1.include ||
        selectedLevels.control1.length === 0
    )
        return true;

    if (selectedLevels.control1.indexOf(vm.control1.control1Level.id) < 0)
        return false;
    if (
        !current.selectionModel.v.control2.include ||
        selectedLevels.control2.length === 0
    )
        return true;
    if (selectedLevels.control2.indexOf(vm.control2.control2Level.id) < 0)
        return false;
    return true;
}

function createXlsxWorksheet(p, def) {
    let vm = this;
    let sheet = new XlsxSheetBuilder();

    addXlsxTitle(p, sheet, def.controlLevels);
    sheet.addBlankLine();
    if (
        p.theReportModel.parms.columnVariable !== null &&
        def.levelsGroupedByRows != null
    )
        vm.$refs.crosstabTableCtl.addXlsxCrosstab(p, sheet, def);
    else vm.$refs.crossTabulationRowOnlyCtl.addXlsxRowOnly(p, sheet, def);

    if (p.theReportModel.parms.showChiSquare && def.c2.chiSquare != null) {
        sheet.addBlankLine();
        xlsxBuilder.writeChiSquare(p, sheet, def.c2.chiSquare);
    }

    if (
        (!p.theSelectionModel.v.control1.include ||
            def.control1LevelId === 0) &&
        (!p.theSelectionModel.v.control2.include || def.control2LevelId === 0)
    ) {
        sheet.addBlankLine();
        commonModelHelper
            .getDatasetTotalRecords(p.theReportModel.recordTotals)
            .forEach(function (theTotal) {
                sheet.addRow([
                    theTotal.label,
                    common.formatNumberNoCommas(theTotal.total, 0)
                ]);
            });
    }

    if (vm.$refs.footnotes) {
        sheet.addBlankLine();
        vm.$refs.footnotes.addFootnotesToSpreadsheet(sheet);
    }

    return sheet.toSheet();
}

function addXlsxTitle(p, sheet, controlLevels) {
    sheet.addRow([
        'Behavioral Risk Factor Surveillance System (BRFSS)',
        null,
        null,
        'https://www.cdc.gov/brfss'
    ]);
    sheet.addBlankLine();
    sheet.addRow([buildHeader(p)]);
    sheet.cellMerges.push({ s: { c: 0, r: 1 }, e: { c: 4, r: 2 } });
    sheet.addBlankLine();

    if (p.theSelectionModel.v.control1.include) {
        sheet.addBlankLine();
        sheet.addRow(['Controlling For']);
        sheet.addRow([
            p.theSelectionModel.v.control1.variable.toString(),
            controlLevels.control1.title
        ]);
        if (p.theSelectionModel.v.control2.include) {
            sheet.addRow([
                p.theSelectionModel.v.control2.variable.toString(),
                controlLevels.control2.title
            ]);
        }
    }
}

function buildHeader(p) {
    return (
        'Cross Tabulation' +
        ' of ' +
        p.theReportModel.parms.row.groupToString() +
        (p.theReportModel.parms.column === null
            ? ''
            : ' by ' + p.theReportModel.parms.column.groupToString()) +
        ', ' +
        commonModelHelper.getLocationsForXlsx(p.theState.selModel.locations) +
        ' ' +
        store.state.selModel.years.asList
    );
}

// function getLeftMarginToRightJustify(margin) {
//   const vm = this;
//   vm.$refs.crosstabWrapperOld.setAttribute("style", "overflow-x: auto; ");
//   let newControlLeft = margin; //Add margin
//   const controlBox = vm.$refs.crosstabWrapperOld.getBoundingClientRect();
//   return -1 * (controlBox.left - newControlLeft);
// }
</script>

<style scoped>
table {
    table-layout: auto;
    /* min-width: 100%; */
}

table td {
    overflow: hidden;
    white-space: nowrap;
    width: 1%;
}

table tbody tr:last-child td {
    border: none;
}

table td.statistic-total {
    text-align: center;
}

table td {
    white-space: nowrap;
}

.statistic-label {
    padding: 0px 10px 0px 10px;
}

table tr.first-row td:nth-child(2) {
    /*border-left: 2px solid;*/
}

table tr:not(.total-group) td.statistic-value {
    border-left: 2px solid;
}

td.statistic-value {
    padding-left: 10px;
}

table tr.first-row td.statistic-cell {
    border-top: 2px solid;
}

table {
    padding-top: 15px;
    /*margin-left: 50px;*/
}

table tr.first-row td {
    padding-top: 4px;
}

table tr.first-row td {
    padding-top: 15px;
}

table tr.last-row td {
    padding-bottom: 15px;
}

@media print {
    .print-header {
        /*width:250px;*/
        display: inline-block;
        font-size: xx-large;
        margin: 0 0 40px 0px;
        width: 100%;
        text-align: center;
    }
}

.dataset-totals {
    margin-top: 15px;
}

.chi-square {
    margin-bottom: 20px;
    margin-top: 5px;
    box-sizing: border-box;
    overflow: auto;
}

.crosstab-wrapper {
    overflow-x: auto;
}

@media print {
    .outer-crosstab-wrapper {
        display: inline;
    }
    .crosstab-wrapper {
        /* overflow-x: visible !important; */
        overflow-x: hidden;
    }

    .chi-square {
        overflow-x: hidden !important;
    }
}

.spacing-row {
    margin-top: 80px;
}
</style>
